import { useControllableState, useEventCallback } from '@flexteam/hooks-common';
import { useMemo } from 'react';
import { useGetFileSourceTypeRestrictions } from './useFileSourceTypeRestrictions.mjs';
import { useUploadTemporaryFileByPreSignedUrl } from './useUploadTemporaryFileByPreSignedUrl.mjs';

const DEFAULT_MAX_COUNT = 50;
const FALLBACK_PARAMS = Object.freeze({
  sourceType: "Unknown"
});
function useFileUploadByPreSignedUrlWithState({
  initialFiles = [],
  keepErrorFiles = true,
  maxFileCount = DEFAULT_MAX_COUNT,
  onDuplicateFile,
  onExceedTheMaxCount,
  onExceedTheMaxSize,
  onExceedTheFileRestrictions,
  onFilesChange,
  sourceType,
  restrictionQueryOptions,
  ...options
} = FALLBACK_PARAMS) {
  const { getRestrictionInfo } = useGetFileSourceTypeRestrictions(
    restrictionQueryOptions
  );
  const restrictionInfo = getRestrictionInfo(sourceType);
  const accept = useMemo(
    () => restrictionInfo && Array.from(restrictionInfo.whiteLists).map((item) => `.${item}`).join(","),
    [restrictionInfo]
  );
  const { mutateAsync: uploadTemporaryFileByPreSignedUrl } = useUploadTemporaryFileByPreSignedUrl(options);
  const [files, setFiles] = useControllableState({
    defaultProp: initialFiles.map((file) => ({
      ...file,
      status: "idle"
    })),
    onChange: (newFiles) => {
      const uploadedFiles = newFiles.filter(
        (file) => file.fileKey !== void 0
      );
      onFilesChange?.(newFiles, uploadedFiles);
    }
  });
  const addFile = useEventCallback((file) => {
    setFiles((prevFiles) => [...prevFiles, file]);
  });
  const removeFile = useEventCallback((targetUuid) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file.uuid !== targetUuid));
  });
  const reset = useEventCallback(
    (exceptUuidList = []) => setFiles(
      (prevFiles) => prevFiles.filter((file) => exceptUuidList.includes(file.uuid))
    )
  );
  const uploads = useEventCallback(
    (newFiles) => {
      if (files.length + newFiles.length > maxFileCount) {
        if (typeof onExceedTheMaxCount === "function") {
          onExceedTheMaxCount();
        }
        return;
      }
      return Promise.all(newFiles.map((newFile) => upload(newFile)));
    }
  );
  const upload = useEventCallback(
    async ({ file, uuid }) => {
      if (validateUnique({ uuid })) {
        if (!validateLimit(file)) {
          if (keepErrorFiles) {
            addFile({
              file,
              uuid,
              name: file.name,
              fileKey: void 0,
              status: "error"
            });
          }
          return;
        }
        addFile({
          file,
          uuid,
          name: file.name,
          fileKey: void 0,
          status: "loading"
        });
        try {
          const { fileKey } = await uploadTemporaryFileByPreSignedUrl({
            file,
            sourceType
          });
          setFiles((prev) => {
            return prev.map(
              (f) => f.uuid === uuid ? {
                ...f,
                status: "idle",
                fileKey
              } : f
            );
          });
        } catch {
          setFiles((prev) => {
            return prev.map(
              (f) => f.uuid === uuid ? {
                ...f,
                status: "error"
              } : f
            );
          });
        }
      }
    }
  );
  const validateLimit = (file) => {
    if (files.length >= maxFileCount) {
      onExceedTheMaxCount?.();
      return false;
    }
    const maxSizeBytes = restrictionInfo?.maxSizeBytes ?? 0;
    if (file.size > maxSizeBytes) {
      const sizeLimit = ((byte) => {
        const kilo = 1024;
        const exponent = Math.floor(Math.log(byte) / Math.log(kilo));
        const formattedResult = new Intl.NumberFormat("ko-KR").format(
          byte / Math.pow(kilo, exponent)
        );
        const units = ["B", "KB", "MB", "GB", "TB"];
        const unit = units[exponent];
        return unit ? formattedResult + unit : (
          /** 여기에 이르게 되는 경우는 잘 없다. */
          new Intl.NumberFormat().format(byte) + units[0]
        );
      })(maxSizeBytes);
      onExceedTheMaxSize?.(sizeLimit);
      return false;
    }
    const allowedFileExtensions = restrictionInfo?.whiteLists ?? /* @__PURE__ */ new Set();
    const hasNoFileType = !file.type;
    if (hasNoFileType) {
      onExceedTheFileRestrictions?.();
      return false;
    }
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const notAllowedFileExtension = !allowedFileExtensions.has(
      fileExtension ?? ""
    );
    if (notAllowedFileExtension) {
      onExceedTheFileRestrictions?.(fileExtension);
      return false;
    }
    return true;
  };
  const validateUnique = ({ uuid }) => {
    const isUnique = files.find((existFile) => existFile.uuid === uuid) === void 0;
    if (!isUnique) {
      onDuplicateFile?.();
    }
    return isUnique;
  };
  return {
    addFile,
    files,
    removeFile,
    reset,
    restrictionInfo,
    accept,
    upload,
    uploads
  };
}

export { useFileUploadByPreSignedUrlWithState as default };
//# sourceMappingURL=useFileUploadByPreSignedUrlWithState.mjs.map
